/**
 * Shopping Cart Module
 *
 * LEGACY - DEAD CODE. The standalone ShoppingCart page has been removed and
 * cart rendering is handled by modules/inline-cart.js (slide-in panel).
 * This module only initializes on pages with templateType 'ShoppingCart',
 * which no longer exists. It is retained solely for export compatibility
 * (window.ECommerceSystem.ShoppingCart). Do not extend.
 *
 * Handles cart display, item management, and quantity controls
 */

import { getStorage, setStorage, clearStorage } from '../core/storage.js';
import { escapeHtml, getAllElements, dispatchCartUpdatedEvent } from '../core/utils.js';
import { t } from '../core/i18n.js';

export class ShoppingCart {
    constructor(system) {
        this.system = system;
        this.cartItemsContainer = null;
        this.totalPriceElement = null;
        this.emptyCartButton = null;
    }

    init() {
        // Only initialize on ShoppingCart pages
        if (typeof window.templateType !== 'undefined' && window.templateType !== 'ShoppingCart') {
            console.log('ShoppingCart: Not a shopping cart page, skipping initialization');
            return;
        }

        // Get DOM elements
        this.cartItemsContainer = document.querySelector('.js-cart-items');
        this.totalPriceElement = document.querySelector('.js-total-price');
        this.emptyCartButton = document.querySelector('.js-empty-cart');

        // Early return if no cart elements found - this page doesn't have a cart
        if (!this.cartItemsContainer) {
            console.log('ShoppingCart: No cart elements found, skipping initialization');
            return;
        }

        console.log('ShoppingCart: Initializing cart functionality');
        this.showProducts();
        this.updateTotalPrice();
        this.setupEmptyCartButton();
    }

    setupEmptyCartButton() {
        if (!this.emptyCartButton) return;

        const hasItems = getStorage(this.system.config.storageKey).length > 0;
        this.emptyCartButton.style.display = hasItems ? 'block' : 'none';
        
        if (hasItems) {
            this.emptyCartButton.addEventListener('click', () => this.emptyCart());
        }
    }

    showProducts() {
        if (!this.cartItemsContainer) return;

        const storageItems = getStorage(this.system.config.storageKey);
        this.cartItemsContainer.innerHTML = '';

        if (storageItems.length === 0) {
            this.cartItemsContainer.innerHTML = `<p class="no-products">${t('cart.empty')}</p>`;
            return;
        }

        storageItems.forEach(item => {
            // Skip Surcharge items - they will be displayed within their related product
            if (item.Type === 'Surcharge' || item.ProductNr === 'PHYSICAL-CARD-FEE') {
                console.log('Skipping Surcharge item - will be displayed in gift card info:', item.ProductName);
                return;
            }
            
            const cartItemElement = this.createCartItemElement(item);
            this.cartItemsContainer.appendChild(cartItemElement);
        });

        this.setupQuantityControls();
    }

    createCartItemElement(item) {
        const total = item.OrderCount * item.ProductPrice;
        
        const cartItem = document.createElement('div');
        cartItem.className = `cart-item js-cart-product js-product-${item.ProductNr}`;
        cartItem.dataset.productId = item.ProductNr;

        // Gift card info (KISS - compact display)
        const giftCardInfo = item.isGiftCard && item.giftCardData ? `
            <div class="gift-card-info">
                <p><strong>${t('cart.giftCard.to')}</strong> ${escapeHtml(item.giftCardData.recipientName)}${item.giftCardData.recipientEmail ? ` (${escapeHtml(item.giftCardData.recipientEmail)})` : ''}</p>
                <p><strong>${t('cart.giftCard.from')}</strong> ${escapeHtml(item.giftCardData.senderName)}</p>
                ${item.giftCardData.giftMessage ? `<p><strong>${t('cart.giftCard.message')}</strong> ${escapeHtml(item.giftCardData.giftMessage)}</p>` : ''}
                    ${item.giftCardData.isPhysicalCard ? `
                        <p><strong>${t('cart.giftCard.physical')}</strong> ${t('cart.giftCard.yes')}</p>
                        <p><strong>Hantering och frakt:</strong> +${item.giftCardData.physicalCardPrice} kr</p>
                        ${item.giftCardData.deliveryAddress ? `
                            <p><strong>${t('cart.giftCard.deliveryAddress')}</strong><br>
                            ${item.giftCardData.deliveryAddress.deliveryName} ${item.giftCardData.deliveryAddress.deliveryLastname}<br>
                            ${item.giftCardData.deliveryAddress.deliveryAddress}<br>
                            ${item.giftCardData.deliveryAddress.deliveryZip} ${item.giftCardData.deliveryAddress.deliveryCity}</p>
                        ` : ''}
                    ` : ''}
            </div>
        ` : '';

        cartItem.innerHTML = `
            ${item.ImageUrl ? `
                <div class="image-wrapper of-wrapper">
                    <img src="${escapeHtml(item.ImageUrl)}" alt="${escapeHtml(item.ProductName)}">
                </div>
            ` : ''}
            <div class="text-wrapper">
                <a href="${escapeHtml(item.ProductUrl || '#')}">
                    <p class="small-title">${escapeHtml(item.ProductName)}</p>
                </a>
                ${giftCardInfo}
                <p class="cart-price"><span class="js-price">${item.ProductPrice}</span> kr</p>
                <p class="cart-id">Artikelnummer: <span class="js-id">${escapeHtml(item.ProductNr)}</span></p>
                <p class="cart-product-total">Totalsumma: <span class="js-product-total">${total}</span> kr</p>
                <p class="cart-taxrate">${escapeHtml(item.TaxRate || '')}</p>
                <p class="cart-amount-title">${t('cart.selectQuantity')}</p>
                <div class="cart-toggle-wrapper">
                    <button type="button" class="cart-decrease js-decrease" data-product-id="${item.ProductNr}">-</button>
                    <input type="number" name="amount-${item.ProductNr}" value="${item.OrderCount}" readonly>
                    <button type="button" class="cart-increase js-increase" data-product-id="${item.ProductNr}">+</button>
                </div>
            </div>
        `;

        return cartItem;
    }

    setupQuantityControls() {
        // Event delegation för bättre prestanda
        this.cartItemsContainer.addEventListener('click', (e) => {
            if (e.target.classList.contains('js-increase')) {
                this.increaseQuantity(e.target.dataset.productId);
            } else if (e.target.classList.contains('js-decrease')) {
                this.decreaseQuantity(e.target.dataset.productId);
            }
        });
    }

    increaseQuantity(productId) {
        const storageItems = getStorage(this.system.config.storageKey);
        const productIndex = storageItems.findIndex(item => item.ProductNr === productId);
        
        if (productIndex !== -1) {
            const product = storageItems[productIndex];
            
            // Check if we should validate against stock
            const trackInventory = product.EffectiveTrackInventory === true || product.EffectiveTrackInventory === 'True';
            const allowBackorders = product.EffectiveAllowBackorders === true || product.EffectiveAllowBackorders === 'True';
            
            if (trackInventory && !allowBackorders) {
                const maxStock = parseInt(product.EffectiveStockQuantity || '0');
                if (product.OrderCount >= maxStock) {
                    // Show inline notification
                    this.showStockLimitNotification(productId, maxStock);
                    console.log('Max stock reached for', product.ProductName, '- cannot increase quantity');
                    return; // Do not increase
                }
            }
            
            storageItems[productIndex].OrderCount += 1;
            setStorage(this.system.config.storageKey, storageItems);
            this.updateProductDisplay(productId);
            this.updateTotalPrice();
            dispatchCartUpdatedEvent(storageItems);
        }
    }

    decreaseQuantity(productId) {
        const storageItems = getStorage(this.system.config.storageKey);
        const productIndex = storageItems.findIndex(item => item.ProductNr === productId);
        
        if (productIndex === -1) return;

        const product = storageItems[productIndex];
        
        if (product.OrderCount > 1) {
            product.OrderCount -= 1;
            setStorage(this.system.config.storageKey, storageItems);
            this.updateProductDisplay(productId);
        } else {
            // Ta bort produkten helt
            storageItems.splice(productIndex, 1);
            setStorage(this.system.config.storageKey, storageItems);
            this.removeProductFromDOM(productId);
        }
        
        this.updateTotalPrice();
        dispatchCartUpdatedEvent(storageItems);
    }

    updateProductDisplay(productId) {
        const product = this.getStorageProduct(productId);
        if (!product) return;

        const productElement = this.cartItemsContainer.querySelector(`.js-product-${productId}`);
        if (!productElement) return;

        const total = product.OrderCount * product.ProductPrice;
        productElement.querySelector('.js-product-total').textContent = total;
        productElement.querySelector(`input[name="amount-${productId}"]`).value = product.OrderCount;
    }

    removeProductFromDOM(productId) {
        const productElement = this.cartItemsContainer.querySelector(`.js-product-${productId}`);
        if (productElement) {
            productElement.remove();
        }
        
        // Kontrollera om varukorgen är tom
        if (getStorage(this.system.config.storageKey).length === 0) {
            this.cartItemsContainer.innerHTML = `<p class="no-products">${t('cart.empty')}</p>`;
            if (this.emptyCartButton) {
                this.emptyCartButton.style.display = 'none';
            }
        }
    }

    updateTotalPrice() {
        if (!this.totalPriceElement) return;

        const storageItems = getStorage(this.system.config.storageKey);
        const total = storageItems.reduce((sum, item) => {
            return sum + (parseFloat(item.ProductPrice) * item.OrderCount);
        }, 0);

        this.totalPriceElement.textContent = total.toFixed(2);
    }

    emptyCart() {
        clearStorage(this.system.config.storageKey);
        this.cartItemsContainer.innerHTML = `<p class="no-products">${t('cart.empty')}</p>`;
        this.updateTotalPrice();
        if (this.emptyCartButton) {
            this.emptyCartButton.style.display = 'none';
        }
        dispatchCartUpdatedEvent([]);
    }

    getStorageProduct(productId) {
        return getStorage(this.system.config.storageKey).find(item => item.ProductNr === productId);
    }

    removeProduct(productId) {
        const storageItems = getStorage(this.system.config.storageKey);
        const filteredItems = storageItems.filter(item => item.ProductNr !== productId);
        setStorage(this.system.config.storageKey, filteredItems);
        dispatchCartUpdatedEvent(filteredItems);
    }

    showStockLimitNotification(productId, maxStock) {
        // Find the product element
        const productElement = this.cartItemsContainer.querySelector(`.js-product-${productId}`);
        if (!productElement) return;

        // Remove any existing notification for this product
        const existingNotification = productElement.querySelector('.stock-limit-notification');
        if (existingNotification) {
            existingNotification.remove();
        }

        // Create notification element
        const notification = document.createElement('div');
        notification.className = 'stock-limit-notification';
        notification.textContent = `Max ${maxStock} st`;

        // Insert notification after the cart-toggle-wrapper
        const toggleWrapper = productElement.querySelector('.cart-toggle-wrapper');
        if (toggleWrapper) {
            toggleWrapper.parentNode.insertBefore(notification, toggleWrapper.nextSibling);
        }

        // Auto-hide after 3 seconds
        setTimeout(() => {
            if (notification.parentNode) {
                notification.remove();
            }
        }, 3000);
    }
}
